import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AdminScenarioSetupService } from './admin-scenario-setup.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-admin-scenario-setup',
    templateUrl: './admin-scenario-setup.component.html',
    styleUrls: ['./admin-scenario-setup.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AdminScenarioSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  // Component specific properties
  modalTitle = "Scenario Setup";
  taxYears: any[] = [];
  jcdKeyList: string[] = ['250'];
  selectedTaxYear: string = '';
  selectedJcdKey: string = '250';

  scenarioEntity: any = {
    TAX_YEAR: '',
    JCD_KEY: '250',
    SCENARIO_TYPE_CODE: '',
    SCENARIO_JURISDICTION: '',
    FED_SCENARIO: '',
    FED_JCD_KEY: '',
    SCENARIO_DESC: '',
    TAX_YEAR_NOT_VALID: false,
    JCD_KEY_NOT_VALID: false,
    SCENARIO_TYPE_CODE_NOT_VALID: false,
    SCENARIO_JURISDICTION_NOT_VALID: false,
    SCENARIO_DESC_NOT_VALID: false
  };

  scenarioEntity_backup: any = {};

  // Data properties
  rowData: any = {};
  filterParams: any = {};
  /** Global runtime params (legacy name; flat hydrated params). */
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  TAX_YEARS: any[] = [];

  constructor(
    private _snackBar: MatSnackBar,
    private adminScenarioSetupService: AdminScenarioSetupService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const ty = this.gtwApp.TAX_YEARS ?? this.gtwGlobal.global?.inputs?.tax_year?.values;
    this.TAX_YEARS = this.buildTaxYearList(ty);
    this.cdr.markForCheck();
  }

  private buildTaxYearList(source: any): Array<{label: string; value: string}> {
    if (!source) return [];
    const groups: any[] = Array.isArray(source) ? source : Object.values(source);
    const byLabel = new Map<string, {label: string; value: string}>();
    for (const group of groups) {
      const items = Array.isArray(group) ? group : [group];
      for (const item of items) {
        if (item && item.label != null && !byLabel.has(String(item.label))) {
          byLabel.set(String(item.label), {
            label: String(item.label),
            value: String(item.value ?? item.label),
          });
        }
      }
    }
    return Array.from(byLabel.values());
  }

  init() {
    try {
      if (this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
        this.taxYears = this.TAX_YEARS;

        // Initialize scenario entity with filter params tax year
        this.scenarioEntity.TAX_YEAR = this.filterParams.tax_year || '';
        this.selectedTaxYear = this.scenarioEntity.TAX_YEAR;
        this.selectedJcdKey = this.scenarioEntity.JCD_KEY;

        // Backup for reset
        this.scenarioEntity_backup = safeJsonInput<any>(JSON.stringify(this.scenarioEntity), null, 'admin-scenario-setup.parse1');

        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    } catch (e) {
      this.showFormLoader = false;
    }
  }

  changeTaxYear() {
    this.scenarioEntity.TAX_YEAR = this.selectedTaxYear;
    this.scenarioEntity.TAX_YEAR_NOT_VALID = false;
  }

  changeJCDKey() {
    this.scenarioEntity.JCD_KEY = this.selectedJcdKey;
    this.scenarioEntity.JCD_KEY_NOT_VALID = false;
  }

  isValid(): boolean {
    // Reset all validation flags
    this.scenarioEntity.TAX_YEAR_NOT_VALID = false;
    this.scenarioEntity.JCD_KEY_NOT_VALID = false;
    this.scenarioEntity.SCENARIO_TYPE_CODE_NOT_VALID = false;
    this.scenarioEntity.SCENARIO_JURISDICTION_NOT_VALID = false;
    this.scenarioEntity.SCENARIO_DESC_NOT_VALID = false;

    let isValid = true;

    if (!this.scenarioEntity.TAX_YEAR || this.scenarioEntity.TAX_YEAR === '') {
      this.scenarioEntity.TAX_YEAR_NOT_VALID = true;
      isValid = false;
    }

    if (!this.scenarioEntity.JCD_KEY || this.scenarioEntity.JCD_KEY === '') {
      this.scenarioEntity.JCD_KEY_NOT_VALID = true;
      isValid = false;
    }

    if (!this.scenarioEntity.SCENARIO_TYPE_CODE || this.scenarioEntity.SCENARIO_TYPE_CODE === '') {
      this.scenarioEntity.SCENARIO_TYPE_CODE_NOT_VALID = true;
      isValid = false;
    }

    if (!this.scenarioEntity.SCENARIO_JURISDICTION || this.scenarioEntity.SCENARIO_JURISDICTION === '') {
      this.scenarioEntity.SCENARIO_JURISDICTION_NOT_VALID = true;
      isValid = false;
    }

    if (!this.scenarioEntity.SCENARIO_DESC || this.scenarioEntity.SCENARIO_DESC === '') {
      this.scenarioEntity.SCENARIO_DESC_NOT_VALID = true;
      isValid = false;
    }

    return isValid;
  }

  save() {
    if (!this.isValid()) {
      this._snackBar.open('Please fill all the required fields', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Create clean data object without validation flags (matching AngularJS structure)
    const cleanScenarioData = {
      TAX_YEAR: this.scenarioEntity.TAX_YEAR,
      JCD_KEY: this.scenarioEntity.JCD_KEY,
      SCENARIO_TYPE_CODE: this.scenarioEntity.SCENARIO_TYPE_CODE,
      SCENARIO_JURISDICTION: this.scenarioEntity.SCENARIO_JURISDICTION,
      FED_SCENARIO: this.scenarioEntity.FED_SCENARIO,
      FED_JCD_KEY: this.scenarioEntity.FED_JCD_KEY,
      SCENARIO_DESC: this.scenarioEntity.SCENARIO_DESC
    };

    // Call service to save scenario setup
    this.adminScenarioSetupService.saveScenarioSetup(
      this.GENERAL_CONFIG.base_url,
      cleanScenarioData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Scenario created successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh event for grid update
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    this.scenarioEntity = safeJsonInput<any>(JSON.stringify(this.scenarioEntity_backup), null, 'admin-scenario-setup.parse2');
    this.selectedTaxYear = this.scenarioEntity.TAX_YEAR;
    this.selectedJcdKey = this.scenarioEntity.JCD_KEY;
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
